import { Avatar, Tooltip } from 'antd';
import TypingIndicator from './TypingIndicator';
import { LoadingOutlined } from '@ant-design/icons';
import { t } from 'i18next';

const translateStatus = (status: string): string => {
  const statusKey = `home.chat.newChat.statuses.${status}`;
  const translated = t(statusKey);
  return translated !== statusKey ? translated : status;
};

const translateMode = (mode: string): string => {
  const modeKey = `home.chat.newChat.modes.${mode}`;
  const translated = t(modeKey);
  return translated !== modeKey ? translated : mode;
};

export const ChatBubble = ({
  username,
  timestamp,
  isSender,
  loading,
  message = '',
  avatar = '/icons/logo.svg',
  status = '',
  mode = '',
}: {
  username: string;
  timestamp: string;
  message: string;
  avatar: string;
  isSender: boolean;
  loading: boolean;
  setHighlightData?: any;
  isGeneratedReference?: boolean;
  referenceError?: string | null;
  status?: string;
  mode?: string;
}) => {
  const handleLinkClick = (fileName: any, path: any) => {
    if (!path || typeof path !== 'string') {
      return;
    }

    try {
      let pdfPath = path;
      let page = null;

      const questionMarkIndex = path.indexOf('?');
      if (questionMarkIndex !== -1) {
        const basePath = path.substring(0, questionMarkIndex);
        const queryString = path.substring(questionMarkIndex + 1);
        const params = new URLSearchParams(queryString);

        page = params.get('page');

        const cleanParams = new URLSearchParams();
        for (const [key, value] of params.entries()) {
          if (!['id', 'index', 'msg_id', 'ref', 'page'].includes(key)) {
            cleanParams.append(key, value);
          }
        }

        pdfPath = basePath;
        if (cleanParams.toString()) {
          pdfPath += '?' + cleanParams.toString();
        }
      }

      const pdfViewerUrl = `/pdfViewer/web/viewer.html?file=${encodeURIComponent(pdfPath)}&force=true&skipUnsavedConfirm=true${page ? `#page=${page}` : ''}`;
      window.open(pdfViewerUrl, '_blank');
    } catch (error) {
      const pdfViewerUrl = `/pdfViewer/web/viewer.html?file=${encodeURIComponent(path)}&force=true&skipUnsavedConfirm=true`;
      window.open(pdfViewerUrl, '_blank');
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: isSender ? 'row-reverse' : 'row',
        alignItems: 'flex-start',
        marginBottom: '15px',
      }}
    >
      <div>
        <Tooltip title={`${username} - ${timestamp}`} placement="top">
          <Avatar
            size={40}
            style={{
              margin: isSender ? '0 0 0 10px' : '0 10px 0 0',
              display: isSender ? 'none' : 'block',
              border: '1px solid #e4e4e4',
              padding: '7px',
            }}
            src={avatar || '/icons/logo.svg'}
          />
        </Tooltip>
      </div>
      <div
        style={{ display: 'flex', flexDirection: 'column', maxWidth: '70%' }}
      >
        {loading && !isSender ? (
          <>
            <TypingIndicator />
            {status && (
              <div
                style={{
                  fontSize: '14px',
                  padding: '8px 15px',
                  marginTop: '8px',
                  borderRadius: '20px 0 20px 20px',
                  backgroundColor: 'transparent',
                  display: 'block',
                }}
              >
                <span>
                  <i>
                    {t('home.chat.newChat.status', {
                      status: translateStatus(status),
                    })}
                  </i>
                </span>
              </div>
            )}
          </>
        ) : (
          <div
            style={{
              fontSize: '16px',
              padding: '10px 15px',
              borderRadius: '20px 0 20px 20px',
              backgroundColor: isSender ? '#EEEEEE' : 'transparent',
              display: 'block',
              alignItems: 'center',
              gap: '10px',
            }}
          >
            {isSender ? (
              <div dangerouslySetInnerHTML={{ __html: message }} />
            ) : (
              <div>
                <div
                  dangerouslySetInnerHTML={{ __html: message }}
                  onClick={e => {
                    const target = e.target as HTMLElement;
                    if (target.tagName === 'A' && target.getAttribute('href')) {
                      e.preventDefault();
                      const href = target.getAttribute('href') || '';
                      const text = target.textContent || target.innerText || '';
                      handleLinkClick(text, href);
                    }
                  }}
                />
                {status && (
                  <>
                    <span>
                      <i>
                        {t('home.chat.newChat.status', {
                          status: translateStatus(status),
                        })}
                      </i>
                    </span>
                    <br />
                  </>
                )}
                {mode && (
                  <>
                    <span>
                      <i>
                        {t('home.chat.newChat.mode', {
                          mode: translateMode(mode),
                        })}
                      </i>
                    </span>
                    <br />
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
